import type { CSSProperties } from "react";
import { strings } from "@/data/strings";
import { SectionHeader } from "@/components/SectionHeader";
import { useInView } from "@/hooks/useInView";
import styles from "./HowItWorks.module.css";

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

const MOCKUPS = [MockNearby, MockProfile, MockContact];

export function HowItWorks() {
  const { ref, inView } = useInView<HTMLOListElement>({
    threshold: 0,
    rootMargin: "0px 0px -25% 0px",
  });

  return (
    <section id="como-funciona" className={styles.section}>
      <div className="container">
        <SectionHeader
          align="center"
          eyebrow={strings.howItWorks.eyebrow}
          title={strings.howItWorks.title}
          subtitle={strings.howItWorks.subtitle}
        />

        <ol ref={ref} className={cx(styles.steps, inView && styles.revealed)}>
          {strings.howItWorks.steps.map((step, i) => {
            const Mockup = MOCKUPS[i];
            return (
              <li
                key={step.number}
                className={styles.step}
                style={{ "--i": i } as CSSProperties}>
                <span className={styles.number} aria-hidden="true">
                  {step.number}
                </span>

                <div
                  className={styles.mockup}
                  role="img"
                  aria-label={step.mockupAlt}>
                  <Mockup />
                </div>

                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepBody}>{step.body}</p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockups — pequeños, estilizados. Representan una pantalla por paso sin
// reproducir todo el chrome del teléfono (eso ya lo hace el Hero).
// ─────────────────────────────────────────────────────────────────────────────

const PROS = strings.hero.mockup.pros;

function MockNearby() {
  return (
    <div className={styles.mockSurface}>
      <div className={styles.mockSearch}>
        <IconSearch />
        <span>{strings.hero.mockup.searchPlaceholder}</span>
      </div>

      <div className={styles.mockFilters}>
        {strings.howItWorks.searchFilters.map((f) => (
          <span key={f} className={styles.mockFilterChip}>
            {f}
          </span>
        ))}
      </div>

      <ul className={styles.mockList}>
        {PROS.slice(0, 3).map((p, i) => (
          <li key={p.name} className={styles.mockRow}>
            <span
              className={cx(
                styles.mockAvatar,
                styles[`mockAvatar${i}` as "mockAvatar0"],
              )}>
              {p.initials}
            </span>
            <span className={styles.mockRowText}>
              <span className={styles.mockRowName}>{p.name}</span>
              <span className={styles.mockRowMeta}>{p.spec}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MockProfile() {
  const pro = PROS[0];
  return (
    <div className={styles.mockSurface}>
      <div className={styles.mockProfileTop}>
        <span className={cx(styles.mockAvatarLg, styles.mockAvatar0)}>
          {pro.initials}
        </span>
        <span className={styles.mockRowName}>{pro.name}</span>
      </div>

      <div className={styles.mockChips}>
        {strings.hero.mockup.profileChips.map((c) => (
          <span key={c} className={styles.mockChip}>
            {c}
          </span>
        ))}
      </div>

      <p className={styles.mockBio}>{strings.hero.mockup.profileBio}</p>
    </div>
  );
}

function MockContact() {
  return (
    <div className={styles.mockSurface}>
      <ul className={styles.mockChannels}>
        {strings.howItWorks.contactChannels.map((c) => (
          <li key={c.kind} className={styles.mockChannel}>
            <span
              className={cx(
                styles.mockChannelIcon,
                styles[`mockChannelIcon_${c.kind}` as "mockChannelIcon_whatsapp"],
              )}>
              <ChannelIcon kind={c.kind} />
            </span>
            <span className={styles.mockRowText}>
              <span className={styles.mockRowName}>{c.label}</span>
              <span className={styles.mockRowMeta}>{c.value}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChannelIcon({
  kind,
}: {
  kind: "whatsapp" | "instagram" | "linkedin" | "x" | "web";
}) {
  switch (kind) {
    case "whatsapp":
      return <IconChatBubble />;
    case "instagram":
      return <IconInstagram />;
    case "linkedin":
      return <IconLinkedIn />;
    case "x":
      return <IconX />;
    case "web":
      return <IconGlobe />;
  }
}

// ─── Icons (outline, currentColor) ─────────────────────────────────────────

function IconSearch() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3-3" />
    </svg>
  );
}

function IconChatBubble() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M21 12a8.5 8.5 0 0 1-12.6 7.4L4 21l1.6-4.4A8.5 8.5 0 1 1 21 12Z" />
    </svg>
  );
}

function IconInstagram() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconLinkedIn() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M8 10v7" />
      <circle cx="8" cy="7" r="0.6" fill="currentColor" stroke="none" />
      <path d="M12 17v-4a3 3 0 0 1 6 0v4" />
    </svg>
  );
}

function IconGlobe() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18" />
      <path d="M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}

function IconX() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
    </svg>
  );
}
